import { useAutoAnimate } from "@formkit/auto-animate/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import { useEffect } from "react";
import plus from "@src/assets/icons/plus.svg";
import Container from "@src/components/shared/Container";
import CountrySelect from "@src/components/shared/CountrySelect";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@src/components/ui/form";
import { Input } from "@src/components/ui/input";
import useZustandStore from "@src/helpers/zustand";
import { GEO_LAT_LETTERS_ONLY } from "@src/helpers/regex";
import { LoggerTypes, useKYCAddUserLog } from "@src/api/useKYCAddUserLog";

const FormSchema = z.object({
  passportCountry: z.number().refine((val) => val > 0, {
    message: "სავალდებულო ველი",
  }),
  otherCountry: z.number(),
  birthCountry: z.number().refine((val) => val, {
    message: "სავალდებულო ველი",
  }),
  birthCity: z.string().refine((val) => val, {
    message: "სავალდებულო ველი",
  }),
  nonCitizen: z.boolean(),
});

function Citizenship() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);
  const [animationParent] = useAutoAnimate();
  const addUserLogMutation = useKYCAddUserLog();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      birthCountry: zData.citizenship.birthCountry,
      passportCountry: zData.citizenship.passportCountry,
      birthCity: zData.citizenship.birthCity,
      otherCountry: zData.citizenship.otherCountry,
      nonCitizen: zData.citizenship.nonCitizen,
    },
  });

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.Citizenship,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(citizenship: z.infer<typeof FormSchema>) {
    setData({
      ...zData,
      citizenship,
    });
    navigate(`/${i18n.language}/address`);
  }

  const otherCountry = form.watch("otherCountry");
  const passportCountry = form.watch("passportCountry");
  const nonCitizen = form.watch("nonCitizen");

  return (
    <Container className="max-md:min-h-[90vh] max-sm:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc
            desc={t("citizenship.desc")}
            title={t("citizenship.title")}
          />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5" ref={animationParent}>
            <div>
              <FormField
                control={form.control}
                name="birthCountry"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <CountrySelect
                          countryId={field.value}
                          forBirth
                          label={t("citizenship.inputs.birthCountry")}
                          setCountryId={(newCountryId) => {
                            field.onChange({
                              target: {
                                value: newCountryId,
                              },
                            });
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="birthCity"
                render={({ field }) => {
                  return (
                    <FormItem className="mt-3">
                      <FormControl>
                        <Input
                          {...field}
                          label={t(`citizenship.inputs.birthCity`)}
                          onChange={(e) => {
                            const { target } = e;
                            if (
                              GEO_LAT_LETTERS_ONLY.test(target.value) ||
                              !target.value
                            ) {
                              field.onChange(e);
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            </div>

            <div>
              <FormField
                control={form.control}
                name="passportCountry"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <CountrySelect
                          countryId={field.value}
                          disabled={nonCitizen}
                          setCountryId={(newCountryId) => {
                            field.onChange({
                              target: {
                                value: newCountryId,
                              },
                            });
                          }}
                          withoutCountryId={otherCountry}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />

              {/* <FormField
                control={form.control}
                name="nonCitizen"
                render={({ field }) => {
                  return (
                    <FormItem className="rounded-6 flex items-center gap-2 bg-white p-4 md:px-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(value) => {
                            form.setValue("passportCountry", value ? 9999 : 0);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 ">
                        {t("citizenship.haveNoCitizenship")}
                      </FormLabel>
                    </FormItem>
                  );
                }}
              /> */}
            </div>

            {Boolean(!nonCitizen && otherCountry === 0) && (
              <div>
                <p className="mb-2 text-sm font-medium">
                  {t("citizenship.haveMoreCitizenship")}
                </p>
                <Button
                  className="w-full"
                  onClick={() => {
                    form.setValue("otherCountry", -1);
                  }}
                  type="button"
                  variant="outline"
                >
                  <div className="flex items-center justify-center gap-2">
                    <img alt="plus" className="h-6 w-6" src={plus} />
                    {t("citizenship.AddCitizenship")}
                  </div>
                </Button>
              </div>
            )}

            {Boolean(otherCountry) && (
              <div className="flex flex-col">
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- disable for now */}
                <p
                  className="text-error-500 mb-1 cursor-pointer self-end font-normal"
                  onClick={() => {
                    form.setValue("otherCountry", 0);
                  }}
                >
                  {t("citizenship.remove")}
                </p>
                <FormField
                  control={form.control}
                  name="otherCountry"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <CountrySelect
                            countryId={field.value}
                            setCountryId={(newCountryId) => {
                              field.onChange({
                                target: {
                                  value: newCountryId,
                                },
                              });
                            }}
                            withoutCountryId={passportCountry}
                          />
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />
              </div>
            )}
          </div>

          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={!form.formState.isValid}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default Citizenship;
